import Modal from '@components/base/Modal'
import { getCroppedImg } from '@libs/imageUtils'
import { useCallback, useEffect, useState } from 'react'
import Cropper, { Area, Point } from 'react-easy-crop'
import { Button } from './Button'
import { v4 } from 'uuid'
import Box from '@components/base/Box'

export interface CropperModalProps {
  originalImageSrc?: string
  setOriginalImageSrc: (src?: string, file?: File) => Promise<void>
  show: boolean
  setShow: (show: boolean) => void
  aspectRatio?: number
  cropShape?: 'rect' | 'round'
}

export const CropperModal = ({
  originalImageSrc,
  setOriginalImageSrc,
  show,
  setShow,
  aspectRatio = 1,
  cropShape = 'rect',
}: CropperModalProps) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [animating, setAnimating] = useState(true)

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  useEffect(() => {
    if (!show) return

    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }, [originalImageSrc, show])

  return (
    <Modal
      show={originalImageSrc != null && show}
      setShow={async () => {
        setShow(false)
        await setOriginalImageSrc()
      }}
      animating={(animating) => {
        setAnimating(animating)
      }}
      mobileFullscreen
    >
      <Box className="relative min-h-0 h-[calc(100dvh-9rem)] sm:h-[60vh] w-full overflow-hidden touch-none">
        {!animating && (
          // Started causing a build error out of the blue, works fine.
          // @ts-ignore
          <Cropper
            image={originalImageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            classes={{ containerClassName: 'z-50' }}
            cropShape={cropShape}
          />
        )}
      </Box>
      <Box className="w-full mt-4">
        <input
          aria-label="Zoom"
          className="w-full accent-forumm-blue"
          max={3}
          min={1}
          onChange={(event) => setZoom(Number(event.target.value))}
          step={0.1}
          type="range"
          value={zoom}
        />
      </Box>
      <Button
        title="Crop"
        className="w-full mt-4"
        onClick={async () => {
          if (originalImageSrc && croppedAreaPixels) {
            const croppedImage = await getCroppedImg(
              originalImageSrc,
              croppedAreaPixels,
              0
            )
            if (!croppedImage?.blob) {
              await setOriginalImageSrc(originalImageSrc)
              setShow(false)
              return
            }
            const file = new File([croppedImage.blob], `${v4()}.jpg`, {
              type: 'image/jpeg',
            })
            await setOriginalImageSrc(croppedImage.url, file)
          } else {
            await setOriginalImageSrc(originalImageSrc)
          }
          setShow(false)
        }}
      />
    </Modal>
  )
}
